'use client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStarSharp } from '@fortawesome/pro-solid-svg-icons';
import { Badge, Button } from '@get-mosh/msh-shared';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { PrismicRichText } from '@prismicio/react';
import classNames from 'classnames';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import React, { ReactNode } from 'react';
/**
 * Props for `PricingColumn`.
 */
export type PricingColumnProps =
    SliceComponentProps<Content.PricingColumnSlice>;

/**
 * Component for "PricingColumn" Slices.
 */
const PricingColumn = ({ slice }: PricingColumnProps): JSX.Element => {
    return (
        <section
            data-slice-type={slice.slice_type}
            data-slice-variation={slice.variation}
            className="mx-auto flex flex-col items-center gap-8 px-4 py-8 md:flex-row md:gap-12 md:py-16"
        >
            <div className="container">
                <div className="flex flex-col items-center gap-2 pb-8 text-center md:pb-12">
                    <h2>{slice.primary.title}</h2>
                    <p className="p1">{slice.primary.sub_heading}</p>
                </div>
                <div className="flex flex-col gap-6 md:w-full md:flex-row md:flex-wrap md:items-stretch">
                    {slice.primary.card.map((feature, index) => (
                        <div
                            key={index}
                            className={classNames(
                                'flex w-full flex-col items-start justify-between gap-2 rounded-2xl border border-grey-200 bg-[#fcfcfc] shadow',
                                {
                                    'md:max-w-[calc(50%-12px)]':
                                        slice.primary.card.length <= 2,
                                    'md:max-w-[calc(33%-18px)]':
                                        slice.primary.card.length === 3,
                                    'md:max-w-[calc(25%-18px)]':
                                        slice.primary.card.length === 4,
                                },
                            )}
                        >
                            <div className="w-full p-4">
                                <div className="relative">
                                    <PrismicNextImage
                                        field={slice.primary.card[index]?.image}
                                        className={classNames(
                                            'mb-4 w-full rounded-lg object-cover',

                                            // image sizes
                                            {
                                                'max-w-[100px]':
                                                    slice.primary.image_size ===
                                                    'small',
                                                'max-w-[200px]':
                                                    slice.primary.image_size ===
                                                    'medium',
                                                'max-w-full':
                                                    slice.primary.image_size ===
                                                    'large',
                                            },

                                            // aspect ratio's
                                            {
                                                'aspect-video':
                                                    slice.primary
                                                        .aspect_ratio ===
                                                    '16:9',
                                                'aspect-4/3':
                                                    slice.primary
                                                        .aspect_ratio === '4:3',
                                                'aspect-square':
                                                    slice.primary
                                                        .aspect_ratio === '1:1',
                                                'aspect-3/4':
                                                    slice.primary
                                                        .aspect_ratio === '3:4',
                                                'aspect-9/16':
                                                    slice.primary
                                                        .aspect_ratio ===
                                                    '9:16',
                                            },
                                        )}
                                    />
                                    {slice.primary.card[index]?.badge ? (
                                        <Badge
                                            variant="fill-dark"
                                            rounded={false}
                                            className={classNames(
                                                'absolute left-4 top-4',
                                                {
                                                    hidden:
                                                        slice.primary
                                                            .image_size ===
                                                            'small' ||
                                                        slice.primary
                                                            .image_size ===
                                                            'medium',
                                                },
                                            )}
                                        >
                                            <FontAwesomeIcon
                                                icon={faStarSharp}
                                                className="mr-2"
                                            />
                                            {slice.primary.card[index]?.badge}
                                        </Badge>
                                    ) : null}
                                </div>
                                <div
                                    className={classNames(
                                        'flex flex-col gap-2',
                                    )}
                                >
                                    <h3>
                                        {slice.primary.card[index]?.heading}
                                    </h3>
                                    {['tag_1', 'tag_2', 'tag_3']
                                        .filter(
                                            (tag) =>
                                                slice.primary.card[index]?.[
                                                    `${tag}_text` as keyof (typeof slice.primary.card)[number]
                                                ],
                                        )
                                        .map((tag) => (
                                            <Badge
                                                key={tag}
                                                variant={
                                                    slice.primary.card[index]?.[
                                                        `${tag}_fill` as keyof (typeof slice.primary.card)[number]
                                                    ]
                                                        ? 'fill-dark'
                                                        : 'outline-dark'
                                                }
                                                rounded={false}
                                                size="small"
                                            >
                                                {
                                                    slice.primary.card[index]?.[
                                                        `${tag}_text` as keyof (typeof slice.primary.card)[number]
                                                    ] as ReactNode
                                                }
                                            </Badge>
                                        )).length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {['tag_1', 'tag_2', 'tag_3']
                                                .filter(
                                                    (tag) =>
                                                        slice.primary.card[
                                                            index
                                                        ]?.[
                                                            `${tag}_text` as keyof (typeof slice.primary.card)[number]
                                                        ],
                                                )
                                                .map((tag) => (
                                                    <Badge
                                                        key={tag}
                                                        variant={
                                                            slice.primary.card[
                                                                index
                                                            ]?.[
                                                                `${tag}_fill` as keyof (typeof slice.primary.card)[number]
                                                            ]
                                                                ? 'fill-dark'
                                                                : 'outline-dark'
                                                        }
                                                        rounded={false}
                                                        size="small"
                                                    >
                                                        {
                                                            slice.primary.card[
                                                                index
                                                            ]?.[
                                                                `${tag}_text` as keyof (typeof slice.primary.card)[number]
                                                            ] as ReactNode
                                                        }
                                                    </Badge>
                                                ))}
                                        </div>
                                    ) : null}
                                    <PrismicRichText
                                        field={
                                            slice.primary.card[index]
                                                ?.description
                                        }
                                        components={{
                                            paragraph: ({ children }) => (
                                                <p className="p1">{children}</p>
                                            ),
                                            listItem: ({ children }) => (
                                                <ul className="list-disc pl-5">
                                                    <li className="p1">
                                                        {children}
                                                    </li>
                                                </ul>
                                            ),
                                            oListItem: ({ children }) => (
                                                <li className="p1 ml-4">
                                                    {children}
                                                </li>
                                            ),
                                            hyperlink: ({ children, node }) => (
                                                <a
                                                    className="font-demi underline"
                                                    href={node.data.url}
                                                >
                                                    {children}
                                                </a>
                                            ),
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="w-full">
                                <Button
                                    variant="ghost"
                                    className="mb-4 ml-4 !justify-start"
                                >
                                    <PrismicNextLink
                                        field={
                                            slice.primary.card[index]
                                                ?.button_link
                                        }
                                    />
                                </Button>
                                <div className="flex flex-row items-baseline gap-1 rounded-b-2xl border-t border-grey-200 bg-grey-100 p-4">
                                    <h3 className="font-demi">
                                        {slice.primary.card[index]?.price}
                                    </h3>
                                    <p className="p1 text-grey-400">
                                        {slice.primary.card[index]?.duration}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default PricingColumn;
